import type { Locale } from "@khazzin/i18n";
import { formatDateTime, formatTime } from "@khazzin/i18n/dates";
import { StatusChip } from "@khazzin/ui/patterns/StatusChip";
import { useServiceWorkerUpdate } from "@khazzin/ui/patterns/serviceWorker";
import { Button } from "@khazzin/ui/primitives/Button";
import { Dialog } from "@khazzin/ui/primitives/Dialog";
import { SegmentedControl } from "@khazzin/ui/primitives/SegmentedControl";
import { type ReactNode, useEffect, useId, useState } from "react";
import { useI18n } from "../../i18n";
import { type PushState, pushState } from "../../modules/push/push";
import { all } from "../../offline/db";
import { hasWork, type PendingWork, pendingWork, useOffline } from "../../offline/Offline";
import { applyTheme, storedTheme, type Theme } from "../../theme";
import { timeZone } from "./Shell";

/**
 * Profile (DA-10-23, EP10-S21): who is signed in — the driver, their delivery
 * company and vehicle — the language and theme, notifications, the sync card
 * (every command still on the phone with its state, and the files waiting to
 * upload), the app version with a waiting update, and sign-out, which warns
 * when queued work would be lost with the purge and offers Sync now first.
 */
const NO_WORK: PendingWork = { commands: 0, files: 0 };

export function Profile({ appVersion }: { appVersion: string }) {
	const { t, locale, setLocale } = useI18n();
	const { day, outbox, online, syncing, syncNow, signOut } = useOffline();
	// The work a sign-out would delete, while the warning is open.
	const [pending, setPending] = useState<PendingWork | null>(null);
	const [leaving, setLeaving] = useState(false);
	const [theme, setTheme] = useState<Theme>(storedTheme);
	const update = useServiceWorkerUpdate();
	const driver = day?.driver;
	const confirming = pending !== null;
	const leave = async () => {
		setLeaving(true);
		try {
			await signOut();
		} finally {
			setLeaving(false);
		}
	};
	/**
	 * ADJ-M03-4: counts what is on the phone at the tap, photos and signatures
	 * still uploading included — a synced delivery can still have its proof
	 * waiting — and warns before the purge deletes any of it.
	 */
	const signOutTapped = async () => {
		setLeaving(true);
		try {
			const work = await pendingWork().catch(() => NO_WORK);
			if (hasWork(work)) {
				setPending(work);
				return;
			}
			await signOut();
		} finally {
			setLeaving(false);
		}
	};
	// While the warning is open, a sync that lands updates it — and closes it
	// once nothing is left to lose.
	// biome-ignore lint/correctness/useExhaustiveDependencies: the triggers are the point
	useEffect(() => {
		if (!confirming) {
			return;
		}
		let live = true;
		void pendingWork()
			.then((work) => live && setPending(hasWork(work) ? work : null))
			.catch(() => undefined);
		return () => {
			live = false;
		};
	}, [outbox, syncing, confirming]);

	return (
		<div className="flex flex-col gap-4">
			<section className="flex flex-col gap-1 card p-4">
				<p className="text-[15px] font-bold text-fg">{driver?.fullName ?? ""}</p>
				{driver !== undefined && (
					<p className="text-[13px] text-fg-muted">
						{[
							driver.companyName,
							driver.vehicle
								? `${t(`driver.profile.vehicle.${driver.vehicle.type}`)} · ${driver.vehicle.plateNumber}`
								: t("driver.profile.noVehicle"),
						].join(" · ")}
					</p>
				)}
			</section>

			<section className="flex flex-col gap-4 card p-4">
				<Setting label={t("driver.profile.language")}>
					{(labelId) => (
						<SegmentedControl<Locale>
							aria-labelledby={labelId}
							value={locale}
							onChange={setLocale}
							options={[
								{ value: "en", label: "English" },
								{ value: "ar", label: "العربية" },
							]}
						/>
					)}
				</Setting>
				<Setting label={t("driver.profile.theme")}>
					{(labelId) => (
						<SegmentedControl<Theme>
							aria-labelledby={labelId}
							value={theme}
							onChange={(next) => {
								applyTheme(next, true);
								setTheme(next);
							}}
							options={[
								{ value: "light", label: t("driver.profile.themes.light") },
								{ value: "dark", label: t("driver.profile.themes.dark") },
								{ value: "system", label: t("driver.profile.themes.system") },
							]}
						/>
					)}
				</Setting>
				<Notifications onGranted={syncNow} />
			</section>

			<SyncCard />

			<div className="flex items-center gap-2">
				<p className="me-auto text-[12px] text-fg-muted">
					{t("driver.profile.version", { version: appVersion })}
				</p>
				{update.available && (
					<>
						<StatusChip tone="info" size="sm">
							{t("driver.profile.updateAvailable")}
						</StatusChip>
						<Button size="sm" onClick={update.apply}>
							{t("common.upgrade.updateNow")}
						</Button>
					</>
				)}
			</div>
			<Button
				variant="danger"
				size="lg"
				loading={leaving && !confirming}
				onClick={() => void signOutTapped()}
			>
				{t("driver.profile.signOut")}
			</Button>
			<Dialog
				open={confirming}
				onOpenChange={(open) => !open && setPending(null)}
				title={t("driver.profile.signOutTitle")}
				description={t("driver.profile.signOutPending", {
					commands: pending?.commands ?? 0,
					files: pending?.files ?? 0,
				})}
				size="sm"
				closeLabel={t("driver.profile.close")}
				footer={
					// Wraps rather than overflowing a 375px screen in Arabic (ADJ-M03-4).
					<div className="flex flex-1 flex-wrap justify-end gap-2">
						<Button variant="ghost" onClick={() => setPending(null)}>
							{t("driver.profile.stay")}
						</Button>
						<Button disabled={!online} loading={syncing} onClick={syncNow}>
							{t("driver.profile.syncNow")}
						</Button>
						<Button variant="danger" loading={leaving} onClick={() => void leave()}>
							{t("driver.profile.signOut")}
						</Button>
					</div>
				}
			/>
		</div>
	);
}

/** A labelled row of Profile's settings. */
function Setting({ label, children }: { label: string; children: (labelId: string) => ReactNode }) {
	const id = useId();
	return (
		<div className="flex flex-col gap-2">
			<p id={id} className="text-[13px] font-bold text-fg">
				{label}
			</p>
			{children(id)}
		</div>
	);
}

/**
 * Push on this phone (EP10-S21). The browser asks only after a tap, so this is
 * where the driver turns it on; the next sync registers the token.
 */
function Notifications({ onGranted }: { onGranted: () => void }) {
	const { t } = useI18n();
	const [state, setState] = useState<PushState>(() => pushState(import.meta.env));
	return (
		<div className="flex items-center gap-3">
			<p className="me-auto flex flex-col text-[13px]">
				<span className="font-bold text-fg">{t("driver.profile.notifications")}</span>
				<span className="text-fg-muted">{t(`driver.profile.push.${state}`)}</span>
			</p>
			{state === "off" && (
				<Button
					onClick={() =>
						void Notification.requestPermission().then(() => {
							const next = pushState(import.meta.env);
							setState(next);
							if (next === "on") {
								onGranted();
							}
						})
					}
				>
					{t("driver.profile.pushTurnOn")}
				</Button>
			)}
		</div>
	);
}

/**
 * Every command still on the phone with its state, the files waiting to
 * upload, and Sync now. Also on the upgrade screen (EP10-S23).
 */
export function SyncCard() {
	const { t, locale } = useI18n();
	const { day, outbox, online, syncing, lastSyncAt, syncNow } = useOffline();
	const [media, setMedia] = useState(0);
	const queued = outbox.filter((e) => e.state === "queued").length;
	// Re-counted as the outbox moves and after each sync: an upload leaves the store.
	// biome-ignore lint/correctness/useExhaustiveDependencies: the triggers are the point
	useEffect(() => {
		let live = true;
		void all("media")
			.then((files) => live && setMedia(files.length))
			.catch(() => undefined);
		return () => {
			live = false;
		};
	}, [outbox, syncing]);
	return (
		<section aria-labelledby="sync-title" className="flex flex-col gap-3 card p-4">
			<div className="flex items-center gap-3">
				<h2 id="sync-title" className="me-auto text-[15px] font-bold text-fg">
					{t("driver.profile.sync")}
				</h2>
				<Button size="lg" disabled={!online} loading={syncing} onClick={syncNow}>
					{t("driver.profile.syncNow")}
				</Button>
			</div>
			<p className="text-[13px] text-fg-muted">
				{lastSyncAt
					? t("driver.profile.lastSync", { at: formatDateTime(lastSyncAt, { locale, timeZone }) })
					: t("driver.profile.neverSynced")}
			</p>
			<p className="text-[13px] text-fg">
				{t("driver.profile.queuedCount", { count: queued })}
				{" · "}
				{t("driver.profile.mediaCount", { count: media })}
			</p>
			{outbox.length === 0 ? (
				<p className="text-[14px] text-fg-muted">{t("driver.profile.nothingQueued")}</p>
			) : (
				<ul className="flex flex-col divide-y divide-border">
					{outbox.map((e) => (
						<li key={e.idempotencyKey} className="flex items-center gap-2 py-2 text-[13px]">
							<span className="me-auto flex flex-col">
								<span className="font-bold text-fg">{t(`driver.command.${e.command}`)}</span>
								<span className="text-fg-muted">
									{[
										e.shipmentId
											? (day?.shipments[e.shipmentId]?.trackingNumber ?? e.shipmentId)
											: null,
										formatTime(e.capturedAt, { locale, timeZone }),
									]
										.filter(Boolean)
										.join(" · ")}
								</span>
							</span>
							<StatusChip tone={e.state === "queued" ? "warning" : "danger"} size="sm">
								{t(`driver.profile.state.${e.state}`)}
							</StatusChip>
						</li>
					))}
				</ul>
			)}
		</section>
	);
}
